import {
  Alert,
  Button,
  Col,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Spin,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { ROLES, User } from '../../../../types';
import { useEffect, useState } from 'react';
import {
  createUser,
  updateUser,
  getRoles,
} from '../../../../utils/httpRequest';
import { useSelector } from 'react-redux';
import { selectCompanies } from '../../../../slices/companies';
import { selectUser, selectIsSuperAdmin } from '../../../../slices/profile';

export interface CreateUserModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isEdit?: boolean;
  user?: User;
}

const CreateUserModal = ({
  open,
  onClose,
  onConfirm,
  isEdit,
  user,
}: CreateUserModalProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [roles, setRoles] = useState<{ id: number; name: string }[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const companies = useSelector(selectCompanies);
  const currentUser = useSelector(selectUser);
  const isSuperAdmin = useSelector(selectIsSuperAdmin);

  const handleConfirm = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const formValues = form.getFieldsValue();
      const data = {
        ...user,
        ...formValues,
        roles: [
          {
            name: formValues.role as ROLES,
          },
        ],
        ...(isSuperAdmin ? {} : { companyID: currentUser?.companyID }),
      };
      await (isEdit ? updateUser(data) : createUser(data));
      onConfirm();
    } catch (err: any) {
      if (err?.errorFields) {
        setError(t('complete_form'));
      } else {
        setError(err?.message || err || t('submit_failed_short'));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    form.resetFields();
    setError('');
    if (user) {
      form.setFieldsValue(user);
    }
  }, [user, open]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setRolesLoading(true);
        const data = await getRoles();
        setRoles(data);
      } catch (err) {
        console.error('Failed to fetch roles:', err);
      } finally {
        setRolesLoading(false);
      }
    };

    if (open) {
      fetchRoles();
    }
  }, [open]);

  const roleOptions = roles.map((role) => ({
    label: role.name.replace('ROLE_', ''),
    value: role.name,
  }));

  return (
    <Modal
      title={
        isEdit ? t('edit_user', { name: user?.username }) : t('add_new_user')
      }
      open={open}
      closable
      onCancel={onClose}
      footer={
        <Flex justify="flex-end" gap={16}>
          <Button type="primary" ghost onClick={onClose} loading={loading}>
            {t('cancel')}
          </Button>
          <Button type="primary" onClick={handleConfirm} loading={loading}>
            {t('confirm')}
          </Button>
        </Flex>
      }
    >
      <Typography.Text type="secondary">
        {t('fill_user_information')}
      </Typography.Text>
      {error && (
        <Alert
          style={{ marginTop: 12 }}
          message={error}
          type="error"
          showIcon
          closable
          onClose={() => setError('')}
        />
      )}
      <Spin spinning={loading || rolesLoading}>
        <Form
          form={form}
          layout="vertical"
          style={{ margin: 20 }}
          onValuesChange={() => setError('')}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('first_name')}
                name="firstName"
                rules={[
                  {
                    required: false,
                    message: t('required_first_name'),
                  },
                ]}
              >
                <Input type="text" placeholder={t('enter')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('last_name')}
                name="lastName"
                rules={[
                  {
                    required: false,
                    message: t('required_last_name'),
                  },
                ]}
              >
                <Input type="text" placeholder={t('enter')} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label={t('role')}
            name="role"
            rules={[
              {
                required: true,
                message: t('required_role'),
              },
            ]}
          >
            <Select options={roleOptions} placeholder={t('select')} />
          </Form.Item>
          {isSuperAdmin && (
            <Form.Item
              label={t('company')}
              name="companyID"
              rules={[
                {
                  required: true,
                  message: t('required_company'),
                },
              ]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={companies.map((i) => ({
                  label: i.name,
                  value: i.companyID,
                }))}
                placeholder={t('select_company')}
              />
            </Form.Item>
          )}
          <Form.Item
            label={t('email')}
            name="email"
            rules={[
              {
                required: true,
                message: t('required_email_lowercase'),
              },
            ]}
          >
            <Input type="text" placeholder={t('enter')} />
          </Form.Item>
          <Form.Item
            label={isEdit ? t('password') : t('initial_password')}
            name="password"
            rules={
              isEdit
                ? []
                : [
                    {
                      required: true,
                      message: t('required_password'),
                    },
                  ]
            }
          >
            <Input.Password
              placeholder={t('enter')}
              visibilityToggle={{
                visible: passwordVisible,
                onVisibleChange: setPasswordVisible,
              }}
            />
          </Form.Item>
          <Form.Item
            label={t('status')}
            name="isActive"
            rules={[
              {
                required: true,
                message: t('required_status'),
              },
            ]}
            initialValue
          >
            <Select
              options={[
                {
                  label: t('active'),
                  value: true,
                },
                { label: t('deactivated'), value: false },
              ]}
              placeholder={t('select')}
            />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default CreateUserModal;
